import {
  formatAppArea,
  formatAppCode,
  PLAY_THRESHOLDS,
} from "@/v2-backoffice-client/usage/usage.constants"
import { describeTrend } from "@/v2-backoffice-client/usage/usage.text"
import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import type {
  UsageScopeMember,
  UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

/**
 * The play's mechanic — what KIND of move this is, independent of the
 * customer-specific sentence. The Portfolio playbook renders these as bold
 * skim-labels so ten accounts can be triaged without reading ten paragraphs.
 */
export type UsageInsightKind =
  | "idle-seats"
  | "newly-quiet"
  | "falling-users"
  | "untouched-areas"
  | "automation"
  | "champions"
  | "steady"
  | "v1-only"
  | "v1-fading"
  | "migration"
  | "v2-pilot"

/** A user called out by name inside an insight text — linkable in the UI. */
export type UsageInsightMention = {
  userId: number
  /** EXACTLY as the name appears in the insight text (substring contract). */
  name: string
}

export type UsageInsight = {
  kind: UsageInsightKind
  tone: "action" | "opportunity" | "positive"
  /** The full play: what is happening and what to do about it. */
  text: string
  /**
   * The play in a few words of data ("9 of 13 seats idle", the names, the
   * areas), for surfaces that show many accounts at once; `text` is the
   * explanation behind it.
   */
  brief: string
  /** Users named in `text` and `brief`, in order of appearance. */
  mentions?: UsageInsightMention[]
}

const UNTOUCHED_AREA_PITCH_LIMIT = 3

function previousWeightedOf(member: UsageScopeMember) {
  return member.WeightedEventsPrev28d ?? member.EventsPrev28d ?? 0
}

/**
 * Turns the scope summary into the plays a sales/CS person should run —
 * plain sentences with names and numbers, ordered by urgency. This is where
 * the "high engagement AND high churn risk" paradox gets spelled out: the
 * score averages the ACTIVE seats, so a few enthusiasts plus a pile of idle
 * licenses reads as both. The renewal risk is the idle licenses (downgrade
 * to fewer seats), and the counter-move is activating them.
 */
export function deriveUsageInsights(
  summary: UsageScopeSummary,
): UsageInsight[] {
  const insights: UsageInsight[] = []
  const T = PLAY_THRESHOLDS
  const weighted = summary.weightedEvents28d ?? summary.events28d
  const previousWeighted = summary.weightedEventsPrev28d
  const quietCount = summary.seatBands.quiet
  const quietShare = summary.seatCount > 0 ? quietCount / summary.seatCount : 0

  if (
    summary.seatCount >= T.idleSeats.minSeats &&
    quietCount >= T.idleSeats.minIdle &&
    quietShare >= T.idleSeats.minIdleShare &&
    summary.activeUsers28d > 0
  ) {
    insights.push({
      kind: "idle-seats",
      tone: "action",
      brief: `${quietCount} of ${summary.seatCount} seats idle`,
      text:
        `Only ${summary.activeUsers28d} of ${summary.seatCount} licenses ${summary.activeUsers28d === 1 ? "is" : "are"} in active use. ` +
        `Engaged users don't protect the renewal while ${quietCount} seat${quietCount === 1 ? "" : "s"} sit idle — ` +
        `the customer can downgrade to fewer licenses. Get the idle seats onboarded before the renewal conversation.`,
    })
  }

  // Only people who did real work before count as gone quiet; one of two
  // seats is half the account, one of ten is a holiday.
  const newlyQuiet = summary.newlyQuiet.filter(
    (member) => previousWeightedOf(member) >= T.newlyQuiet.minPreviousWeighted,
  )
  if (
    newlyQuiet.length > 0 &&
    (newlyQuiet.length >= T.newlyQuiet.minPeople ||
      newlyQuiet.length / Math.max(summary.seatCount, 1) >=
        T.newlyQuiet.minSeatShare)
  ) {
    insights.push({
      kind: "newly-quiet",
      tone: "action",
      brief: nameList(newlyQuiet),
      text:
        `${nameList(newlyQuiet)} ${newlyQuiet.length === 1 ? "was" : "were"} active last period but ` +
        `${newlyQuiet.length === 1 ? "has" : "have"} gone quiet — a check-in now is cheaper than a churned seat at renewal.`,
      mentions: nameMentions(newlyQuiet),
    })
  }

  // A fall counts from a real base, and matters when the account itself is
  // falling or the fallers are a good share of the active people.
  const falling = summary.falling.filter(
    (member) => previousWeightedOf(member) >= T.falling.minPreviousWeighted,
  )
  if (
    falling.length > 0 &&
    ((summary.trendPct ?? 0) <= T.falling.accountTrendPct ||
      falling.length / Math.max(summary.activeUsers28d, 1) >=
        T.falling.minActiveShare)
  ) {
    insights.push({
      kind: "falling-users",
      tone: "action",
      brief: nameList(falling),
      text: `Usage from ${nameList(falling)} is dropping sharply — find out what changed.`,
      mentions: nameMentions(falling),
    })
  }

  if (
    summary.untouchedAreas.length > 0 &&
    summary.activeUsers28d >= T.untouched.minActive &&
    (summary.avgScore ?? 0) >= T.untouched.minScore &&
    summary.topAreas.length >= T.untouched.minUsedAreas &&
    weighted >= T.untouched.minWeighted &&
    summary.churnRisk.level !== "high"
  ) {
    const pitch = summary.untouchedAreas
      .slice(0, UNTOUCHED_AREA_PITCH_LIMIT)
      .map(formatAppArea)
      .join(", ")
    const interest = summary.topAreas[0]
      ? ` — a natural extension of their ${formatAppArea(summary.topAreas[0].area).toLowerCase()} work`
      : ""
    insights.push({
      kind: "untouched-areas",
      tone: "opportunity",
      brief: pitch,
      text: `Nobody has touched ${pitch}. A demo or workshop on these is an expansion angle${interest}.`,
    })
  }

  if (summary.automationSuspects.length > 0) {
    insights.push({
      kind: "automation",
      tone: "opportunity",
      brief: nameList(summary.automationSuspects),
      text: `${nameList(summary.automationSuspects)} show${summary.automationSuspects.length === 1 ? "s" : ""} scripted-usage patterns — open the API-license conversation.`,
      mentions: nameMentions(summary.automationSuspects),
    })
  }

  if (
    summary.trendPct != null &&
    summary.trendPct >= T.champions.minTrendPct &&
    summary.champions.length > 0 &&
    weighted >= T.champions.minWeighted &&
    previousWeighted >= T.champions.minPreviousWeighted
  ) {
    insights.push({
      kind: "champions",
      tone: "positive",
      brief: `${describeTrend(summary.trendPct)} · ${nameList(summary.champions)}`,
      text: `Usage is ${describeTrend(summary.trendPct)} on the four weeks before. Anchor the renewal on ${nameList(summary.champions)} — they can champion an expansion internally.`,
      mentions: nameMentions(summary.champions),
    })
  }

  if (
    insights.length === 0 &&
    summary.activeUsers28d >= T.steady.minActive &&
    (summary.avgScore ?? 0) >= T.steady.minScore &&
    quietShare <= T.steady.maxIdleShare &&
    weighted >= T.steady.minWeighted
  ) {
    insights.push({
      kind: "steady",
      tone: "positive",
      brief: `${summary.activeUsers28d} of ${summary.seatCount} seats active, engagement ${Math.round(summary.avgScore ?? 0)}`,
      text: "Broad, steady usage across the seats — a reference-customer conversation may be worth more than an upsell push right now.",
    })
  }

  return insights
}

/**
 * Product-fit plays from the per-app split. Console v1 and v2 are sold
 * separately, so the mix itself is sales raw material: v1-heavy accounts
 * are v2 demo candidates, v1 fading without v2 pickup is churn (not
 * migration), and early v2 seats inside a v1 account are the migration
 * beachhead to support. Scopes with no v1 history get no callouts — being
 * v2-only is the modern default, not a signal.
 */
export function deriveAppInsights(appMix: UsageAppMixEntry[]): UsageInsight[] {
  const v1 = appMix.find((entry) => entry.app === "v1-console")
  const v2 = appMix.find((entry) => entry.app === "v2-console")
  if (!v1) return []

  const insights: UsageInsight[] = []
  const v1Label = formatAppCode("v1-console")
  const v2Label = formatAppCode("v2-console")
  const v2Weighted = v2?.weighted28d ?? 0
  // What Console v1 did in the window before, read back from the trend:
  // a handful of clicks collapsing to one is not a product moving.
  const v1Previous =
    v1.trendPct != null && v1.trendPct > -100
      ? v1.weighted28d / (1 + v1.trendPct / 100)
      : null
  const v1Fading =
    v1.trendPct != null &&
    v1.trendPct <= -30 &&
    (v1Previous == null || v1Previous >= PLAY_THRESHOLDS.consoleFloor)
  const v2Active = v2Weighted > 0
  const v2Growing =
    v2Active && (v2?.trendPct == null || (v2.trendPct ?? 0) >= 20)
  const v2Carries = v2Weighted >= v1.weighted28d

  if (
    v1.weighted28d >= PLAY_THRESHOLDS.v1Only.minWeighted &&
    (v1.activeSeats ?? 0) >= PLAY_THRESHOLDS.v1Only.minActiveSeats &&
    !v2Active
  ) {
    const seats =
      v1.activeSeats != null
        ? `${v1.activeSeats} active ${v1Label} seat${v1.activeSeats === 1 ? "" : "s"}`
        : `their ${v1Label} usage`
    insights.push({
      kind: "v1-only",
      tone: "opportunity",
      brief:
        v1.activeSeats != null
          ? `${v1.activeSeats} active ${v1Label} seat${v1.activeSeats === 1 ? "" : "s"}, none in ${v2Label}`
          : `${v1Label} only`,
      text: `All console usage is in ${v1Label} — ${v2Label} is untouched. ${capitalize(seats)} make${v1.activeSeats === 1 ? "s" : ""} a warm audience for a ${v2Label} demo.`,
    })
  }

  // Fading v1 is disengagement only while v1 is still the console in use;
  // once v2 carries the work, the account has moved.
  if (v1Fading && !v2Growing && !v2Carries) {
    insights.push({
      kind: "v1-fading",
      tone: "action",
      brief: `${v1Label} down ${Math.abs(v1.trendPct ?? 0).toFixed(0)} %, no ${v2Label} pickup`,
      text: `${v1Label} usage is down ${Math.abs(v1.trendPct ?? 0).toFixed(0)}% with no ${v2Label} pickup — that reads as disengagement, not migration. Check in before renewal.`,
    })
  }

  if (v1Fading && v2Growing) {
    insights.push({
      kind: "migration",
      tone: "positive",
      brief: `${v1Label} → ${v2Label} under way`,
      text: `Usage is shifting from ${v1Label} to ${v2Label} — a migration under way. Make sure the ${v2Label} onboarding sticks so it lands as an upgrade, not churn.`,
    })
  }

  if (
    !v1Fading &&
    v1.weighted28d >= PLAY_THRESHOLDS.consoleFloor &&
    v2Weighted >= PLAY_THRESHOLDS.consoleFloor &&
    !v2Carries
  ) {
    const seats =
      v2?.activeSeats != null && v2.activeSeats > 0
        ? `${v2.activeSeats} seat${v2.activeSeats === 1 ? "" : "s"} already active in ${v2Label}`
        : `${v2Label} activity is starting`
    insights.push({
      kind: "v2-pilot",
      tone: "opportunity",
      brief:
        v2?.activeSeats != null && v2.activeSeats > 0
          ? `${v2.activeSeats} seat${v2.activeSeats === 1 ? "" : "s"} in ${v2Label}, ${v1Label} still ahead`
          : `${v2Label} starting, ${v1Label} still ahead`,
      text: `${capitalize(seats)} while ${v1Label} still carries most of the usage — support these pilot users, they anchor the migration story.`,
    })
  }

  return insights
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const NAME_LIST_LIMIT = 3

/**
 * The linkable mentions for a member call-out — the SAME first members, in
 * the SAME order and with the SAME name strings as {@link nameList} renders,
 * so a mention is always an exact substring of the insight text.
 */
function nameMentions(members: UsageScopeMember[]): UsageInsightMention[] {
  return members.slice(0, NAME_LIST_LIMIT).map((member) => ({
    userId: member.UserId,
    name: formatMemberName(member),
  }))
}

function nameList(members: UsageScopeMember[]) {
  const named = nameMentions(members).map((mention) => mention.name)
  const overflow = members.length - named.length
  return overflow > 0
    ? `${named.join(", ")} +${overflow} more`
    : named.join(", ")
}

/** Short display name for member call-outs ("Ola N.", falls back to email). */
function formatMemberName(member: UsageScopeMember) {
  const first = member.FirstName?.trim()
  const last = member.LastName?.trim()
  if (first || last) {
    return [first, last ? `${last[0]}.` : null].filter(Boolean).join(" ")
  }
  return member.Email ?? `User ${member.UserId}`
}
